import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getPrimaryProductImageUrl } from '../utils/productImages';
import { guestBagReactSnapshotRef } from '../utils/mergeGuestCartToApi';
import { GUEST_BAG_IMPORTED_EVENT } from '../utils/cartGuestBridge';

const BagContext = createContext();

export { BagContext };

export const GUEST_BAG_UPDATED_EVENT = 'sheenwearGuestBagUpdated';

export const useBag = () => {
  const context = useContext(BagContext);
  if (!context) {
    throw new Error('useBag must be used within a BagProvider');
  }
  return context;
};

export const BagProvider = ({ children }) => {
  const [bagItems, setBagItems] = useState([]);
  const [bagCount, setBagCount] = useState(0);
  const [bagTotal, setBagTotal] = useState(0);

  useEffect(() => {
    guestBagReactSnapshotRef.current = bagItems;
  }, [bagItems]);

  useEffect(() => {
    const onImported = (e) => {
      const items = e.detail;
      if (Array.isArray(items)) setBagItems(items);
    };
    window.addEventListener(GUEST_BAG_IMPORTED_EVENT, onImported);
    return () => window.removeEventListener(GUEST_BAG_IMPORTED_EVENT, onImported);
  }, []);

  // Cross-tab guest bag sync (localStorage writes in another tab)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'sheenwear_bag') return;
      if (!e.newValue) {
        setBagItems([]);
        return;
      }
      try {
        const parsed = JSON.parse(e.newValue);
        if (Array.isArray(parsed)) setBagItems(parsed);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Load bag from localStorage on app start
  useEffect(() => {
    const savedBag = localStorage.getItem('sheenwear_bag');
    if (savedBag) {
      try {
        const bagData = JSON.parse(savedBag);
        setBagItems(Array.isArray(bagData) ? bagData : []);
      } catch (error) {
        console.error('Error parsing saved bag data:', error);
        localStorage.removeItem('sheenwear_bag');
      }
    }
  }, []);

  // Update bag count and total when bagItems change
  useEffect(() => {
    const count = bagItems.reduce((total, item) => total + (Number(item.quantity) || 0), 0);
    const total = bagItems.reduce(
      (sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 0),
      0
    );

    setBagCount(count);
    setBagTotal(total);

    if (bagItems.length > 0) {
      localStorage.setItem('sheenwear_bag', JSON.stringify(bagItems));
    } else {
      localStorage.removeItem('sheenwear_bag');
    }

    window.dispatchEvent(new CustomEvent(GUEST_BAG_UPDATED_EVENT, { detail: bagItems }));
  }, [bagItems]);

  const addToBag = (product, selectedSize = null, selectedColor = null, quantity = 1) => {
    const productKey = product.id ?? product._id;
    const itemId = `${productKey}_${selectedSize || 'default'}_${selectedColor || 'default'}`;

    const existingItem = bagItems.find((item) => item.itemId === itemId);

    if (existingItem) {
      setBagItems((items) =>
        items.map((item) =>
          item.itemId === itemId
            ? { ...item, quantity: (Number(item.quantity) || 0) + quantity }
            : item
        )
      );
    } else {
      const newItem = {
        itemId,
        id: productKey,
        _id: product._id,
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice,
        image: getPrimaryProductImageUrl(product) || product.image,
        selectedSize,
        selectedColor,
        quantity,
        category: product.category,
        subcategory: product.subcategory,
      };

      setBagItems((items) => [...items, newItem]);
    }
  };

  const removeFromBag = (itemId) => {
    setBagItems((items) => items.filter((item) => item.itemId !== itemId));
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromBag(itemId);
      return;
    }

    setBagItems((items) =>
      items.map((item) =>
        item.itemId === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const clearBag = useCallback(() => {
    setBagItems([]);
    localStorage.removeItem('sheenwear_bag');
  }, []);

  const getItemQuantity = (productId, selectedSize = null, selectedColor = null) => {
    const itemId = `${productId}_${selectedSize || 'default'}_${selectedColor || 'default'}`;
    const item = bagItems.find((row) => row.itemId === itemId);
    return item ? Number(item.quantity) || 0 : 0;
  };

  const isInBag = (productId, selectedSize = null, selectedColor = null) => {
    return getItemQuantity(productId, selectedSize, selectedColor) > 0;
  };

  const getBagItemsByCategory = (category) => {
    return bagItems.filter((item) => item.category === category);
  };

  const getTotalSavings = () => {
    return bagItems.reduce((total, item) => {
      if (item.originalPrice && item.originalPrice > item.price) {
        return total + (item.originalPrice - item.price) * (Number(item.quantity) || 0);
      }
      return total;
    }, 0);
  };

  const value = {
    bagItems,
    bagCount,
    bagTotal,
    addToBag,
    removeFromBag,
    updateQuantity,
    clearBag,
    getItemQuantity,
    isInBag,
    getBagItemsByCategory,
    getTotalSavings,
  };

  return <BagContext.Provider value={value}>{children}</BagContext.Provider>;
};
